import React from "react";
import styles from "./NotesList.module.css";

/* ---------- Icons ---------- */

const NoteIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const ClockIcon = ({ size = 10 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CalendarIcon = ({ size = 10 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
);

const EmptyIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/* ---------- Helpers ---------- */

const getInitials = (firstname = "", lastname = "") => {
  const first = firstname?.trim()?.[0] || "";
  const last = lastname?.trim()?.[0] || "";
  const initials = (first + last).toUpperCase();
  return initials || "?";
};

/** Returns a short relative time label and a "fresh" flag (<1h) */
const getRelativeTime = (dateString) => {
  if (!dateString) return { label: "", fresh: false };
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return { label: "À l'instant", fresh: true };
  if (diffMin < 60) return { label: `Il y a ${diffMin} min`, fresh: true };
  if (diffHr < 24) return { label: `Il y a ${diffHr} h`, fresh: false };
  if (diffDays === 1) return { label: "Hier", fresh: false };
  if (diffDays < 7) return { label: `Il y a ${diffDays} j`, fresh: false };
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return { label: `Il y a ${weeks} sem.`, fresh: false };
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return { label: `Il y a ${months} mois`, fresh: false };
  }
  const years = Math.floor(diffDays / 365);
  return { label: `Il y a ${years} an${years > 1 ? "s" : ""}`, fresh: false };
};

/** Always returns the exact date/time: "15 mars 2026 · 14:32" */
const getExactTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  const sameYear = date.getFullYear() === now.getFullYear();

  /* Same day → show only the time */
  if (sameDay) {
    return date.toLocaleString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  /* Same year → no year */
  if (sameYear) {
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  /* Older → full date */
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/** Long version used in tooltip */
const getFullDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/* ---------- Component ---------- */

export default function NotesList({ notes = [] }) {
  const userId = localStorage.getItem("userId");
  const hasNotes = notes && notes.length > 0;

  return (
    <div className={styles.container}>
      {/* Section header */}
      <div className={styles.header}>
        <span className={styles.headerIcon}>
          <NoteIcon />
        </span>
        <h3 className={styles.headerTitle}>Notes internes</h3>
        {hasNotes && (
          <span className={styles.headerCount}>{notes.length}</span>
        )}
      </div>

      {/* Thread */}
      {!hasNotes ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <EmptyIcon />
          </div>
          <p className={styles.emptyText}>Aucune note interne pour le moment</p>
        </div>
      ) : (
        <div className={styles.thread}>
          {notes.map((note, index) => {
            const agent = note?.agentSAV;
            const isSelf = String(agent?.id) === String(userId);
            const initials = getInitials(agent?.firstname, agent?.lastname);
            const fullName = `${agent?.firstname || ""} ${
              agent?.lastname || ""
            }`.trim();

            const relative = getRelativeTime(note?.dateCreation);
            const exact = getExactTime(note?.dateCreation);
            const fullDate = getFullDate(note?.dateCreation);

            return (
              <div
                key={note?.id ?? index}
                className={`${styles.note} ${
                  isSelf ? styles.noteSelf : styles.noteOther
                }`}
              >
                <div
                  className={`${styles.avatar} ${
                    isSelf ? styles.avatarSelf : styles.avatarOther
                  }`}
                  title={fullName}
                >
                  {initials}
                </div>

                <div className={styles.noteBody}>
                  <div className={styles.metaRow}>
                    <span className={styles.authorName}>
                      {fullName || "Utilisateur"}
                    </span>
                    {isSelf && <span className={styles.youBadge}>Vous</span>}
                  </div>

                  <div
                    className={`${styles.bubble} ${
                      isSelf ? styles.bubbleSelf : styles.bubbleOther
                    }`}
                  >
                    {note?.contenu}
                  </div>

                  <div className={styles.timestamp} title={fullDate}>
                    <span
                      className={`${styles.relativeTime} ${
                        relative.fresh ? styles.relativeTimeFresh : ""
                      }`}
                    >
                      <ClockIcon size={10} />
                      {relative.label}
                    </span>
                    <span className={styles.timeSeparator} />
                    <span className={styles.exactTime}>
                      <CalendarIcon size={10} />
                      {exact}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}